import { useState, useEffect, useRef } from "react";
import { useAuth } from "./AuthContext";
import "./TemplateEditor.css";

const ANSWER_TYPES = ["text", "single_choice", "multi_choice", "number", "photo", "postcode"];

function emptyQuestion(order) {
  return {
    questionId: `q_${Date.now()}_${order}`,
    order,
    prompt: "",
    answerType: "text",
    options: [],
    required: true,
  };
}

export default function TemplateEditor() {
  const { auth } = useAuth();
  const [projectTypeInput, setProjectTypeInput] = useState("");
  const [loadedProjectType, setLoadedProjectType] = useState("");
  const [questions, setQuestions] = useState([emptyQuestion(1)]);
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [wasDrafted, setWasDrafted] = useState(false);
  const [existingTypes, setExistingTypes] = useState([]);
  const requestIdRef = useRef(0);

  useEffect(() => {
    fetch("/api/admin/intake-templates", {
      headers: { Authorization: `Bearer ${auth.token}` },
      cache: "no-store",
    })
      .then((res) => res.json())
      .then((data) => setExistingTypes(data.map((t) => t.projectType)))
      .catch(() => {});
  }, [auth.token]);

  const loadTemplate = async (type) => {
    const thisRequestId = ++requestIdRef.current;
    setWasDrafted(false);
    if (!type) {
      setQuestions([emptyQuestion(1)]);
      setLoadedProjectType("");
      return;
    }
    try {
      const res = await fetch(`/api/admin/intake-templates/${type}`, {
        headers: { Authorization: `Bearer ${auth.token}` },
        cache: "no-store",
      });
      if (thisRequestId !== requestIdRef.current) return;

      if (res.ok) {
        const data = await res.json();
        setQuestions(data.questions);
        setLoadedProjectType(type);
      } else {
        setQuestions([emptyQuestion(1)]);
        setLoadedProjectType(type);
      }
    } catch {
      if (thisRequestId !== requestIdRef.current) return;
      setQuestions([emptyQuestion(1)]);
    }
  };

  const handleDraft = async () => {
    if (!loadedProjectType) return;
    setStatus(null);
    setDrafting(true);
    try {
      const res = await fetch("/api/admin/intake-templates/draft", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth.token}`,
        },
        cache: "no-store",
        body: JSON.stringify({ projectType: loadedProjectType }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Draft failed");
      setQuestions(data.questions);
      setWasDrafted(true);
      setStatus({
        type: "success",
        message: "Draft generated by AI — review and edit before saving.",
      });
    } catch (err) {
      setStatus({ type: "error", message: err.message });
    } finally {
      setDrafting(false);
    }
  };

  const updateQuestion = (index, field, value) => {
    const next = [...questions];
    next[index] = { ...next[index], [field]: value };
    setQuestions(next);
    setWasDrafted(false);
  };

  const updateOptions = (index, value) => {
    const opts = value.split(",").map((s) => s.trim()).filter(Boolean);
    updateQuestion(index, "options", opts);
  };

  const addQuestion = () => {
    setQuestions([...questions, emptyQuestion(questions.length + 1)]);
    setWasDrafted(false);
  };

  const removeQuestion = (index) => {
    setQuestions(questions.filter((_, i) => i !== index));
    setWasDrafted(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setStatus(null);
    setSaving(true);
    try {
      const res = await fetch("/api/admin/intake-templates", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth.token}`,
        },
        cache: "no-store",
        body: JSON.stringify({
          projectType: loadedProjectType,
          questions: questions.map((q, i) => ({ ...q, order: i + 1 })),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Save failed");
      setStatus({ type: "success", message: `Saved as version ${data.mappingVersion}` });
      setWasDrafted(false);
      if (!existingTypes.includes(loadedProjectType)) {
        setExistingTypes([...existingTypes, loadedProjectType]);
      }
    } catch (err) {
      setStatus({ type: "error", message: err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="te-wrap">
      <h2>Intake Question Templates</h2>

      <div className="te-field">
        <label>Project type</label>
        <input
          list="existing-types"
          value={projectTypeInput}
          onChange={(e) => setProjectTypeInput(e.target.value)}
          onBlur={(e) => loadTemplate(e.target.value.trim())}
          placeholder="e.g. redecoration_interior"
        />
        <datalist id="existing-types">
          {existingTypes.map((t) => (
            <option key={t} value={t} />
          ))}
        </datalist>
      </div>

      {loadedProjectType && (
        <>
          <button
            type="button"
            className="te-draft-btn"
            onClick={handleDraft}
            disabled={drafting}
          >
            {drafting ? "Drafting..." : "✨ Draft with AI"}
          </button>

          {wasDrafted && (
            <div className="te-draft-banner">
              Drafted content — review and edit before saving.
            </div>
          )}

          <form onSubmit={handleSave}>
            {questions.map((q, i) => (
              <div className="te-question-card" key={q.questionId}>
                <div className="te-question-header">
                  <strong>Question {i + 1}</strong>
                  <button
                    type="button"
                    className="te-remove-btn"
                    onClick={() => removeQuestion(i)}
                  >
                    Remove
                  </button>
                </div>

                <div className="te-field">
                  <label>Prompt</label>
                  <input
                    value={q.prompt}
                    onChange={(e) => updateQuestion(i, "prompt", e.target.value)}
                    required
                  />
                </div>

                <div className="te-field">
                  <label>Answer type</label>
                  <select
                    value={q.answerType}
                    onChange={(e) => updateQuestion(i, "answerType", e.target.value)}
                  >
                    {ANSWER_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>

                {(q.answerType === "single_choice" || q.answerType === "multi_choice") && (
                  <div className="te-field">
                    <label>Options (comma separated)</label>
                    <input
                      value={(q.options || []).join(", ")}
                      onChange={(e) => updateOptions(i, e.target.value)}
                    />
                  </div>
                )}

                <div className="te-field">
                  <label>
                    <input
                      type="checkbox"
                      checked={q.required}
                      onChange={(e) => updateQuestion(i, "required", e.target.checked)}
                      style={{ width: "auto", marginRight: "0.5rem" }}
                    />
                    Required
                  </label>
                </div>
              </div>
            ))}

            <button type="button" className="te-add-btn" onClick={addQuestion}>
              + Add question
            </button>

            <button type="submit" className="te-save-btn" disabled={saving}>
              {saving ? "Saving..." : "Save template"}
            </button>

            {status && (
              <div className={`te-status ${status.type}`}>{status.message}</div>
            )}
          </form>
        </>
      )}

      {!loadedProjectType && (
        <p style={{ color: "#9397ab", fontSize: "0.85rem" }}>
          Type a project type above and click away (or press Tab) to load or start a new template.
        </p>
      )}
    </div>
  );
}
